import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import API from '../utils/tenantApi';
import { useAuth } from '../context/AuthContext';

export default function TenantInvitePage() {
  const { token } = useParams();
  const { login } = useAuth();
  const navigate = useNavigate();
  const [invite, setInvite] = useState(null);
  const [loadError, setLoadError] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);

  useEffect(() => {
    API.get(`/tenant/invite/${token}`)
      .then(({ data }) => setInvite(data))
      .catch(err => setLoadError(err.response?.data?.error || 'This invite link is invalid or has expired.'))
      .finally(() => setFetching(false));
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password !== confirm) {
      setError('Passwords do not match');
      return;
    }
    setError('');
    setLoading(true);
    try {
      const { data } = await API.post(`/tenant/invite/${token}`, { password });
      login(data.token, { ...data.user, role: 'tenant' });
      navigate('/tenant/consent');
    } catch (err) {
      setError(err.response?.data?.error || 'Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  if (fetching) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-sm text-slate-500">Loading your invite...</div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4">
        <div className="max-w-md w-full text-center">
          <p className="text-slate-700 font-semibold mb-2">Invite not found</p>
          <p className="text-sm text-slate-500">{loadError}</p>
          <Link to="/" className="mt-4 inline-block text-sm text-brand-600 hover:underline">
            Return home
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-2 mb-4">
            <div className="w-8 h-8 bg-brand-600 rounded-lg flex items-center justify-center">
              <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5}
                  d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
              </svg>
            </div>
            <span className="font-bold text-slate-800 text-lg">RentFriendly</span>
          </Link>
          <h1 className="text-2xl font-bold text-slate-800">
            Welcome, {invite?.tenantName}
          </h1>
          <p className="text-slate-500 text-sm mt-1">
            Set a password to access your rental portal
          </p>
        </div>

        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-8">
          <div className="mb-5 p-3 bg-brand-50 border border-brand-200 rounded-lg">
            <p className="text-sm text-brand-800">
              Your landlord has invited you to view your rent balance online.
              Create a password to get started — your login email will be{' '}
              <span className="font-medium">{invite?.tenantEmail}</span>.
            </p>
          </div>

          {error && (
            <div className="mb-4 p-3 bg-amber-50 border border-amber-200 text-amber-800 rounded-lg text-sm">{error}</div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">Choose a password</label>
              <input
                type="password"
                value={password}
                onChange={e => setPassword(e.target.value)}
                required
                minLength={6}
                placeholder="At least 6 characters"
                className="w-full px-4 py-2.5 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">Confirm password</label>
              <input
                type="password"
                value={confirm}
                onChange={e => setConfirm(e.target.value)}
                required
                placeholder="Repeat your password"
                className="w-full px-4 py-2.5 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent"
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-brand-600 hover:bg-brand-700 disabled:opacity-60 text-white py-2.5 rounded-lg font-semibold text-sm transition-colors mt-2"
            >
              {loading ? 'Setting up...' : 'Access my portal'}
            </button>
          </form>

          <p className="text-center text-sm text-slate-400 mt-4">
            Already set a password?{' '}
            <Link to="/tenant/login" className="text-brand-600 hover:underline">Sign in here</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
